import React from "react";
import useFileNameLength from "../../../../hooks/useFileNameLength";
import { OTHER_QUALIFICATIONS_LEVEL } from "../../../../models/Onboarding/onboarding-values";
import { ICandidateDetails } from "../../../../store/superadmin/types";
import { getCountryName } from "../../../../utils/documentHelper";
import { formatFileName } from "../../../../utils/stringUtils";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Download from "../../../UI/Icons/Download";

interface ClientQualificationProps {
  showQualifications: boolean;
  reviewCandidateData: ICandidateDetails | null;
  convertToTitleCase: (str: string) => string;
}

const ClientQualification: React.FC<ClientQualificationProps> = ({
  showQualifications,
  reviewCandidateData,
  convertToTitleCase,
}) => {
  const { maxLength, preserveEndChars } = useFileNameLength(true);
  const qualificationAcceptOptions = reviewCandidateData?.qualifications
    ?.qualification_accept_options
    ? JSON.parse(
        reviewCandidateData?.qualifications?.qualification_accept_options
      )
    : [];
  const alternativePositions = reviewCandidateData?.qualifications
    ?.alternative_positions
    ? JSON.parse(reviewCandidateData?.qualifications?.alternative_positions)
    : [];
  const candidateDocumentList = reviewCandidateData?.documents || [];

  const firstAidCertificates = candidateDocumentList.find(
    (file: any) => file.document_type === "first_aid_certificate"
  );
  const courseProgressionLetter = candidateDocumentList.find(
    (file: any) => file.document_type === "course_progression_letter"
  );
  const workingWitCchildrenCheck = candidateDocumentList.find(
    (file: any) => file.document_type === "working_with_children_check"
  );
  const teachersRegistration = candidateDocumentList.find(
    (file: any) => file.document_type === "teachers_registration"
  );
  const postGraduateDiploma = candidateDocumentList.find(
    (file: any) => file.document_type === "post_graduate_diploma"
  );
  const mandatoryReportingTraining = candidateDocumentList.find(
    (file: any) => file.document_type === "mandatory_reporting_training"
  );
  const foodHandlerCertificate = candidateDocumentList.find(
    (file: any) => file.document_type === "food_handler_certificate"
  );
  const cookingQualification = candidateDocumentList.find(
    (file: any) => file.document_type === "cooking_qualification"
  );
  const australianWorkVisa = candidateDocumentList.find(
    (file: any) => file.document_type === "australian_work_visa"
  );
  const permanentResidencyVisa = candidateDocumentList.find(
    (file: any) => file.document_type === "permanent_residency_visa"
  );
   const certificate_iii = candidateDocumentList.find(
    (file: any) => file.document_type === "certificate_iii"
  );
   const diploma = candidateDocumentList.find(
    (file: any) => file.document_type === "diploma"
  );
    const educatorTeacherPositions = (() => {
    const val = reviewCandidateData?.qualifications?.educator_teacher_positions;
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try { return JSON.parse(val); } catch { return []; }
  })();

  const handleDownloadDocument = (url: string | null) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "document.pdf"); // Set the file name for download
      link.target = "_blank"; // Open in a new tab
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error("No URL provided for download.");
    }
  };

  const renderDocumentSection = (document: any, label: string) => {
    if (!document) return null;
    return (
      <div className="d-flex flex-column gap-10">
        <div className="fs-13 text-grey-medium">{label}:</div>
        <div className="d-flex">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={() => {
              handleDownloadDocument(document.file_path);
            }}
          >
            <Download />
            {formatFileName(document.file_name, maxLength, preserveEndChars)}
          </CustomButton>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className={`d-flex flex-column gap-20 fw-400 text-black ${
          showQualifications ? "" : "d-none"
        }`}
      >
        <div className="fs-22">Qualifications</div>
        <div className="d-flex flex-column gap-5">
          <div className="fs-13 text-grey-medium">
            Which qualification do you have in Early Childhood Education?
          </div>
          <div className="fs-16">
            {reviewCandidateData?.qualifications?.qualification ===
            "certificate_iii"
              ? OTHER_QUALIFICATIONS_LEVEL.CERTIFICATE_III
              : convertToTitleCase(
                  reviewCandidateData?.qualifications?.qualification ??
                    "No data"
                )}
          </div>
        </div>

        {(reviewCandidateData?.qualifications?.qualification ===
          "bachelor_or_degree" ||
          reviewCandidateData?.qualifications?.qualification ===
            "post_graduate_diploma") &&
          qualificationAcceptOptions.length > 0 && (
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 text-grey-medium">
                What other qualification levels in Early Childhood Education would you be open to accepting?
              </div>
              <div className="fs-16">
                {qualificationAcceptOptions.length > 0
                  ? qualificationAcceptOptions.join(", ")
                  : ""}
              </div>
            </div>
          )}

        <div className="d-flex flex-column gap-5">
          <div className="fs-13 text-grey-medium">
            Where did you complete your studies in Early Childhood Education?
          </div>
          <div className="fs-16">
            {reviewCandidateData?.qualifications?.studies_completed_location ===
            "other"
              ? getCountryName(convertToTitleCase(
                  reviewCandidateData?.qualifications
                    ?.other_study_selected_country ?? "No data"
                ))
              : convertToTitleCase(
                  reviewCandidateData?.qualifications
                    ?.studies_completed_location ?? "No data"
                )}
          </div>
        </div>

        {reviewCandidateData?.qualifications?.studies_completed_location ===
          "new_zealand" && (
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">is ACECQA approved</div>
            <div className="fs-16">
              {reviewCandidateData?.qualifications?.acecqa_approved === 1
                ? "Yes"
                : "No"}
            </div>
          </div>
        )}

        {reviewCandidateData?.qualifications?.qualification === "diploma" && (
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">
              Would you be open to accepting jobs at a Certificate III Educator level?
            </div>
            <div className="fs-16">
              {reviewCandidateData?.qualifications
                ?.diploma_jobs_at_certificate_level === "Yes"
                ? "Yes"
                : "No"}
            </div>
          </div>
        )}

        {alternativePositions.length > 0 || educatorTeacherPositions.length > 0 ? (
          <>
            <div className="bg-off-white min-h-1px max-w-484px"></div>
            <div className="fs-22">Position Types</div>
            {
              reviewCandidateData?.qualifications?.qualification !== "no_qualifications" && (
                <div className="d-flex flex-column gap-5">
                  <div className='text-grey-medium fs-13'>Educator/Teacher Positions:</div>
                  <div className="fs-16">
                    {educatorTeacherPositions.length > 0
                      ? educatorTeacherPositions
                          .filter(
                            (item: string) => !["1", "2", "5"].includes(item)
                          )
                          .join(", ") || "No data"
                      : "No data"}
                  </div>
                </div>
              )
            }
            <div className="d-flex flex-column  gap-5">
              <div className='text-grey-medium fs-13'>Alternative Positions:</div>
              <div className="fs-16">
                {alternativePositions.length > 0
                  ? alternativePositions
                      .filter(
                        (item: string) => !["1", "2", "5"].includes(item)
                      )
                      .join(", ") || "No data"
                  : "No data"}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="bg-off-white min-h-1px max-w-484px"></div>
            <div className="fs-22">Position Types</div>
            <div className='fs-16'>No Data</div>
          </>
        )}

        <div className="d-flex flex-column gap-5">
          <div className="fs-16px fw-700">Key Documents</div>
          {!firstAidCertificates &&
            !mandatoryReportingTraining &&
            !foodHandlerCertificate &&
            !workingWitCchildrenCheck &&
            !teachersRegistration &&
            !postGraduateDiploma &&
            !permanentResidencyVisa &&
            !australianWorkVisa &&
            !certificate_iii && (
              <div className="d-flex">
                <div className="fs-16 fw-400 text-grey-medium">
                  No Documents Found
                </div>
              </div>
            )}

          {reviewCandidateData?.profile?.working_rights === "australian_visa_holder" &&
          renderDocumentSection(australianWorkVisa, "Australian Work Visa")}
          {reviewCandidateData?.profile?.working_rights === "australian_permanent_resident" && renderDocumentSection(permanentResidencyVisa, "Permanent Residency Visa")}
          {renderDocumentSection(postGraduateDiploma, "Post Graduate Diploma")}
           {renderDocumentSection(teachersRegistration, "Teachers Registration")}
           {renderDocumentSection(diploma, "Diploma")}
          {renderDocumentSection(firstAidCertificates, "First Aid Certificate")}
          {renderDocumentSection(courseProgressionLetter, "Course Progression Letter")}
           {renderDocumentSection(
            certificate_iii,
            "Certificate III"
          )}
          {renderDocumentSection(
            mandatoryReportingTraining,
            "Mandatory Reporting Training"
          )}
         {renderDocumentSection(
            workingWitCchildrenCheck,
            "Working with Children Check"
          )}
          {renderDocumentSection(
            cookingQualification,
            "Cooking Qualification"
          )}
           {renderDocumentSection(
            foodHandlerCertificate,
            "Food Handler Certificate"
          )}
        </div>
      </div>
    </>
  );
};

export default ClientQualification;
